import type { VehicleInstruction } from '@/lib/services/redemption';

/**
 * The `itemInfo` fields one vehicle-instruction answer writes.
 *
 * `instruction` clears to `''` rather than `null` because it is an `info` field
 * and the update endpoint MERGES `itemInfo` — `JSON.stringify` drops an
 * `undefined` key entirely, and the empty string is the contract `infoPatch`
 * already uses for a cleared `info` field.
 */
export interface VehicleInstructionChange {
  instruction: VehicleInstruction | '';
  transferBeneficiaries?: never[];
  hasContraryIntention?: false;
}

/**
 * **What answering a vehicle's instruction must write** — the whole rule, in one
 * place (VALE-1363), and the sibling of `shareInstructionChange` and
 * `shareOwnershipChange`.
 *
 * A vehicle's instruction is not a lone field: leaving
 * `transfer-to-beneficiary` has to take the transfer list AND the contrary-
 * intention flag with it, or a `Sell` record still carries the beneficiaries and
 * the contrary intention of a choice the lawyer has just abandoned.
 *
 * That cascade lived inline in `vehicleBlocks`' `onInstructionChange`, which was
 * fine while the kept `VehicleInstructionSection` was the only way to answer.
 * The AI-extract review now answers it too, as an ordinary `select` row in its
 * detail block, so the rule has to sit where both can reach it.
 *
 * **It is the mirror of the share rule, not a copy of it**, and the asymmetry is
 * deliberate: a share allocation is a unit split a lawyer may be mid-way through
 * and come back to, so `shareInstructionChange` preserves it; a vehicle's is a
 * yes/no list that means nothing against `Sell`, so this clears it. Neither
 * behaviour is new here — each is the one its own call site already had.
 */
export function vehicleInstructionChange(
  next: VehicleInstruction | null
): VehicleInstructionChange {
  if (next === 'transfer-to-beneficiary') return { instruction: next };

  return {
    instruction: next ?? '',
    transferBeneficiaries: [],
    hasContraryIntention: false
  };
}
